import React, { useMemo } from 'react';

/**
 * BarList — horizontal ranked bar list (e.g. "top students").
 *
 * Semantics note: unlike the plotted charts in this folder, BarList is not a
 * single graphic — it is a ranked list whose rows can be interactive. It is
 * therefore exposed as an ordered list with a group label rather than as
 * role="img", so screen-reader users can walk the rows and activate them. Every
 * value is real text, so nothing is encoded in color alone.
 */

export interface BarListItem {
  id: number | string;
  label: string;
  sublabel?: string;
  value: number;
  secondary?: string;
}

export interface BarListProps {
  items: BarListItem[];
  onItemClick?: (id: number | string) => void;
}

const fmt = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const safe = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);

const initialOf = (label: string): string => {
  const trimmed = (label || '').replace(/^\s+/, '');
  return trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : 'U';
};

const BarList: React.FC<BarListProps> = ({ items, onItemClick }) => {
  const rows = useMemo(
    () => items.map((it) => ({ ...it, value: safe(it.value) })),
    [items]
  );

  const max = useMemo(() => {
    let m = 0;
    for (let i = 0; i < rows.length; i++) if (rows[i].value > m) m = rows[i].value;
    return m;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center sm:py-10">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bmlc-gold-50 ring-1 ring-bmlc-gold-200">
          <svg
            className="h-6 w-6 text-bmlc-gold-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
            />
          </svg>
        </div>
        <p className="text-xs text-gray-400 sm:text-sm">No data for this period</p>
      </div>
    );
  }

  const clickable = typeof onItemClick === 'function';

  return (
    <ol
      className="space-y-1"
      aria-label={`Ranked list of ${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}, highest first`}
    >
      {rows.map((row, i) => {
        const pct = max > 0 ? Math.max(2, (row.value / max) * 100) : 0;

        const body = (
          <>
            {/* Rank */}
            <span className="w-4 flex-shrink-0 text-right font-serif text-xs font-bold text-bmlc-navy-300 sm:w-5 sm:text-sm">
              {i + 1}
            </span>

            {/* Avatar-style initial in a gold ring */}
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-bmlc-gold-50 ring-2 ring-bmlc-gold-300/70 sm:h-9 sm:w-9">
              <span className="font-serif text-[10px] font-bold text-bmlc-gold-700 sm:text-xs">
                {initialOf(row.label)}
              </span>
            </span>

            {/* Label + sublabel + proportional bar */}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-bmlc-navy-900 sm:text-sm">
                {row.label}
              </span>
              {row.sublabel ? (
                <span className="mt-0.5 block truncate text-[10px] text-gray-500 sm:text-[11px]">
                  {row.sublabel}
                </span>
              ) : null}
              <span
                className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-bmlc-navy-50"
                aria-hidden="true"
              >
                <span
                  className="block h-full rounded-full bg-navy-sheen"
                  style={{ width: `${pct}%` }}
                />
              </span>
            </span>

            {/* Value, right-aligned in the display serif */}
            <span className="flex flex-shrink-0 flex-col items-end pl-1">
              <span className="font-serif text-base font-bold leading-none text-bmlc-navy-900 sm:text-lg">
                {fmt(row.value)}
              </span>
              {row.secondary ? (
                <span className="mt-1 whitespace-nowrap text-[10px] text-gray-500">
                  {row.secondary}
                </span>
              ) : null}
            </span>
          </>
        );

        const rowClass =
          'flex w-full items-center gap-2.5 rounded-xl border border-transparent px-2 py-2.5 text-left sm:gap-3 sm:px-3';

        return (
          <li key={row.id}>
            {clickable ? (
              <button
                type="button"
                onClick={() => {
                  if (onItemClick) onItemClick(row.id);
                }}
                className={`${rowClass} card-interactive bg-white`}
              >
                {body}
              </button>
            ) : (
              <div className={`${rowClass} transition-colors hover:bg-bmlc-navy-50/60`}>
                {body}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default BarList;
